import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Clock, SquareIcon, Calendar, CheckCircle2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency } from "@/lib/utils";

interface ServiceDetails {
  id?: string;
  name: string;
  description?: string;
  type: "cleaning" | "floorcare";
  pricingMethod: "hourly" | "squareFootage";
  frequency: string;
  serviceDays: string[];
  hourlyRate?: number;
  estimatedHours?: number;
  squareFootage?: number;
  ratePerSqFt?: number;
  productionRate?: number;
  overheadPercentage?: number;
  profitMargin?: number;
  totalPrice: number;
}

interface ViewCleaningServiceSheetProps {
  isOpen: boolean;
  onClose: () => void;
  service: ServiceDetails;
}

export function ViewCleaningServiceSheet({
  isOpen,
  onClose,
  service,
}: ViewCleaningServiceSheetProps) {
  // Format frequency for display
  const formatFrequency = (frequency: string) => {
    if (frequency.includes("_per_")) {
      return frequency.replace("_per_", " per ").replace("_", " ");
    }

    // Handle standard frequency formats
    switch (frequency) {
      case "daily":
        return "Daily (Mon-Fri)";
      case "weekly":
        return "Weekly";
      case "biweekly":
        return "Bi-Weekly";
      case "monthly":
        return "Monthly";
      case "quarterly":
        return "Quarterly";
      case "semiannually":
        return "Semi-Annually";
      case "annually":
        return "Annually";
      default:
        return frequency.replace("_", " ");
    }
  };

  // Calculate services per month based on frequency
  const getServicesPerMonth = (frequency: string) => {
    if (frequency.includes("per_week")) {
      const timesPerWeek = parseInt(frequency.split("_")[0]);
      return timesPerWeek * 4.33; // Average weeks per month
    } else if (frequency.includes("per_month")) {
      const timesPerMonth = parseInt(frequency.split("_")[0]);
      return timesPerMonth;
    }

    // Handle standard frequency formats
    switch (frequency) {
      case "daily":
        return 22; // business days in a month
      case "weekly":
        return 4.33; // weeks in a month
      case "biweekly":
        return 2.17; // bi-weekly in a month
      case "monthly":
        return 1; // once per month
      case "quarterly":
        return 4 / 12; // 4 services per year / 12 months
      case "semiannually":
        return 2 / 12; // 2 services per year / 12 months
      case "annually":
        return 1 / 12; // 1 service per year / 12 months
      default:
        return 1;
    }
  };

  // Calculate single service cost
  const getSingleServiceCost = () => {
    if (service.pricingMethod === "hourly" && service.hourlyRate && service.estimatedHours) {
      return service.hourlyRate * service.estimatedHours;
    } else if (
      service.pricingMethod === "squareFootage" &&
      service.squareFootage &&
      service.ratePerSqFt
    ) {
      return service.squareFootage * service.ratePerSqFt;
    } else if (service.totalPrice) {
      // If we have a total price but can't calculate from components,
      // try to derive the single service cost from the total price and frequency
      const servicesPerMonth = getServicesPerMonth(service.frequency);
      if (servicesPerMonth > 0) {
        return service.totalPrice / servicesPerMonth;
      }
    }
    // Return null instead of 0 to avoid rendering a stray "0"
    return null;
  };

  // Calculate services per month
  const servicesPerMonth = getServicesPerMonth(service.frequency);

  // Calculate single service cost
  const singleServiceCost = getSingleServiceCost();

  // Calculate base monthly price (before overhead and profit)
  const baseMonthlyPrice =
    service.pricingMethod === "squareFootage"
      ? service.totalPrice // For square footage, the total is already monthly
      : singleServiceCost !== null
        ? singleServiceCost * servicesPerMonth
        : service.totalPrice; // For hourly, multiply by services per month or use totalPrice as fallback

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>
            {service.type === "cleaning" ? "Cleaning" : "Floorcare"} Service Details
          </SheetTitle>
          <SheetDescription>View details for {service.name} service</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            {/* Service Information */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{service.name}</h3>
                <Badge variant="outline" className="bg-blue-50 text-blue-600">
                  {service.type === "cleaning" ? "Cleaning" : "Floorcare"}
                </Badge>
              </div>

              {service.description && (
                <div className="text-gray-600">
                  <p>{service.description}</p>
                </div>
              )}
            </div>

            <Separator />

            {/* Pricing Method */}
            <div className="space-y-2">
              <h4 className="font-medium">Pricing Method</h4>
              <Badge variant="outline" className="bg-blue-50 text-blue-600">
                {service.pricingMethod === "hourly" ? "Hourly Rate" : "Square Footage"}
              </Badge>
            </div>

            {/* Pricing Details */}
            {service.pricingMethod === "hourly" ? (
              <div className="mt-4">
                <h4 className="mb-4 font-medium">Hourly Rate Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-md bg-gray-50 p-3">
                    <div className="text-sm text-gray-500">Hourly Rate</div>
                    <div className="text-lg font-semibold">
                      {formatCurrency(service.hourlyRate || 0)}/hr
                    </div>
                  </div>
                  <div className="rounded-md bg-gray-50 p-3">
                    <div className="text-sm text-gray-500">Estimated Hours</div>
                    <div className="text-lg font-semibold">
                      {service.estimatedHours ? `${service.estimatedHours} hrs` : "Not specified"}
                    </div>
                  </div>
                </div>

                {/* Hours Calculation Section - Only show if both values are greater than 0 */}
                {(service.productionRate ?? 0) > 0 && (service.squareFootage ?? 0) > 0 && (
                  <div className="mt-4 rounded-md bg-blue-50 p-3">
                    <div className="mb-2 text-sm font-medium">Hours Calculation</div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-600">Square Footage</div>
                        <div className="font-medium">{service.squareFootage} sq ft</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Production Rate</div>
                        <div className="font-medium">{service.productionRate} sq ft/hr</div>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-blue-600">
                      Calculated Hours:{" "}
                      {(service.squareFootage! / service.productionRate!).toFixed(2)} hrs
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <h4 className="font-medium">Square Footage Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-md bg-gray-50 p-3">
                    <div className="text-sm text-gray-500">Square Footage</div>
                    <div className="text-lg font-semibold">{service.squareFootage} sq ft</div>
                  </div>
                  <div className="rounded-md bg-gray-50 p-3">
                    <div className="text-sm text-gray-500">Rate per Sq Ft</div>
                    <div className="text-lg font-semibold">
                      ${(service.ratePerSqFt || 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                {service.frequency === "5_per_week" && (
                  <div className="mt-1 text-sm text-blue-600">
                    Square footage pricing is fixed at 5 days per week
                  </div>
                )}
              </div>
            )}

            <Separator />

            {/* Service Schedule */}
            <div className="space-y-4">
              <h4 className="font-medium">Service Schedule</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-md bg-gray-50 p-3">
                  <div className="text-sm text-gray-500">Frequency</div>
                  <div className="text-lg font-semibold">{formatFrequency(service.frequency)}</div>
                </div>
                <div className="rounded-md bg-gray-50 p-3">
                  <div className="text-sm text-gray-500">Services per Month</div>
                  <div className="text-lg font-semibold">{servicesPerMonth.toFixed(2)}</div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm text-gray-500">Service Days</div>
                <div className="flex flex-wrap gap-2">
                  {service.serviceDays.map((day) => (
                    <Badge key={day} variant="outline" className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {day}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <Separator />

            {/* Pricing Summary */}
            <div className="space-y-4">
              <h4 className="font-medium">Pricing Summary</h4>

              <div className="space-y-2">
                {service.pricingMethod === "hourly" ? (
                  <>
                    {singleServiceCost !== null && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Single Service Cost:</span>
                          <span className="font-medium">{formatCurrency(singleServiceCost)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Services per Month:</span>
                          <span className="font-medium">× {servicesPerMonth.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Base Monthly Price:</span>
                      <span className="font-medium">{formatCurrency(baseMonthlyPrice)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Square Footage:</span>
                      <span className="font-medium">{service.squareFootage} sq ft</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Rate per Sq Ft:</span>
                      <span className="font-medium">${(service.ratePerSqFt || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Monthly Price (5 days/week):</span>
                      <span className="font-medium">{formatCurrency(baseMonthlyPrice)}</span>
                    </div>
                  </>
                )}

                {((typeof service.overheadPercentage === "number" &&
                  service.overheadPercentage > 0) ||
                  (typeof service.profitMargin === "number" && service.profitMargin > 0)) && (
                  <>
                    <Separator className="my-2" />

                    {typeof service.overheadPercentage === "number" &&
                      service.overheadPercentage > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Overhead ({service.overheadPercentage}%):
                          </span>
                          <span className="font-medium">
                            {formatCurrency(baseMonthlyPrice * (service.overheadPercentage / 100))}
                          </span>
                        </div>
                      )}

                    {typeof service.profitMargin === "number" && service.profitMargin > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          Profit Margin ({service.profitMargin}%):
                        </span>
                        <span className="font-medium">
                          {formatCurrency(
                            (baseMonthlyPrice +
                              baseMonthlyPrice *
                                ((typeof service.overheadPercentage === "number"
                                  ? service.overheadPercentage
                                  : 0) /
                                  100)) *
                              (service.profitMargin / 100)
                          )}
                        </span>
                      </div>
                    )}
                  </>
                )}

                <Separator className="my-2" />

                <div className="flex justify-between text-lg font-bold">
                  <span>Total Monthly Price:</span>
                  <span className="text-blue-600">{formatCurrency(service.totalPrice)}</span>
                </div>

                {/* Show annual price */}
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Annual Price:</span>
                  <span>{formatCurrency(service.totalPrice * 12)}</span>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button variant="outline" className="w-full" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
